import { createApi, retry } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery, { getToken, ResponseData } from '../root';
import { ChatbotRTKService } from './chatbot-rtk.services';
import { IInvoice, IInvoiceResponse } from './chatbot-rtk.type';

const baseUrl = `${import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : '/api'}`;

const baseQuery = retry(
  axiosBaseQuery({
    baseUrl,
  }),
  {
    maxRetries: 0,
  }
);

export const BillingRTKService = createApi({
  reducerPath: 'BillingRTKService',
  baseQuery,
  tagTypes: [
    'TotalShortenedLinks',
    'TotalShortenedLinksVisits',
    'ShortLinks',
    'CompareStats',
    'Profile',
    'VerifyPayment',
    'ClientPlans',
    'PlanStatus',
    'PlanSubscriptionHistory',
    'PlanCards',
    'PlanDetailedInfo',
    'PersonalLinks',
    'PlanInfo',
    'AddCard',
    'SesssionProvider',
  ],
  endpoints: builder => ({
    getClientPlans: builder.query<any, void>({
      query: () => ({
        url: `/server/client/plan`,
        method: 'GET',
      }),
      providesTags: ['ClientPlans'],
      transformResponse: (responseData: ResponseData) => responseData.element,
    }),
    getPlanInfo: builder.query<any, void>({
      query: () => ({
        url: `/billing/plan-info`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'PlanInfo' }],
      transformResponse: (responseData: ResponseData) => responseData.element,
    }),
    addCard: builder.mutation<any, { plan: string; redirectUrl: string }>({
      query: ({ plan, redirectUrl }) => ({
        url: `/billing/add-card/${plan}?redirectUrl=${redirectUrl}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: (responseData: ResponseData) => responseData.element,
      invalidatesTags: (result, error, arg) => (result ? [{ type: 'PlanInfo' }] : []),
    }),
    redirectToPayMod: builder.mutation<any, { plan: string; redirectUrl: string }>({
      query: ({ plan, redirectUrl }) => ({
        url: `/billing/payment/${plan}?redirectUrl=${redirectUrl}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: (responseData: ResponseData) => responseData?.element?.redirect,
      invalidatesTags: (result, error, arg) => (result ? [{ type: 'PlanInfo' }] : []),
    }),
    postCancelSubscription: builder.mutation<any, { plan: string }>({
      query: ({ plan }) => ({
        url: `/billing/${plan}/cancel-subscription`,
        method: 'POST',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: (result, error, arg) => (result ? [{ type: 'PlanDetailedInfo', id: arg.plan }, { type: 'PlanStatus' }, { type: 'PlanInfo' }] : []),
    }),
    validatePromotionCode: builder.mutation<any, { code: string }>({
      query: ({ code }) => ({
        url: `/billing/promo-code`,
        method: 'POST',
        headers: {
          'x-auth-token': getToken(),
        },
        data: {
          code: code,
        },
      }),
    }),
    putMakeCardAsDefault: builder.mutation<any, { plan: string; payment_method_id: string }>({
      query: ({ plan, payment_method_id }) => ({
        url: `/billing/${plan}/${payment_method_id}/update-subscription-card`,
        method: 'PUT',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: (result, error, arg) => (result ? [{ type: 'PlanInfo' }] : []),
    }),
    removeSubscriptionCard: builder.mutation<any, { plan: string; payment_method_id: string; planCardsLength: number }>({
      query: ({ plan, payment_method_id, planCardsLength }) => ({
        url: `/billing/${plan}/${payment_method_id}/remove-payment-method`,
        method: 'DELETE',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: (result, error, arg) =>
        result && arg.planCardsLength > 1 ? [{ type: 'PlanCards', id: arg.plan }, { type: 'PlanDetailedInfo', id: arg.plan }, { type: 'PlanInfo' }] : [],
    }),
    getInvoice: builder.mutation<IInvoiceResponse, IInvoice>({
      query: ({ invoice_id }) => ({
        url: `/billing/${invoice_id}/invoice`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: (responseData: ResponseData) => responseData.element,
      invalidatesTags: (result, error, arg) => (result ? [{ type: 'PlanInfo' }] : []),
    }),
    getSessionProvideList: builder.query<any, void>({
      query: () => ({
        url: `/provider-payment/session`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['SesssionProvider'],
      transformResponse: (responseData: ResponseData) => responseData.element,
    }),
    providerPaymentPayMod: builder.mutation<
      any,
      {
        redirectUrl: string;
        amount: number;
        currency: string;
        provider: string;
      }
    >({
      query: ({ redirectUrl, amount, currency, provider }) => ({
        url: `/billing/provider-payment?redirectUrl=${redirectUrl}&amount=${amount}&currency=${currency}&provider=${provider}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: (responseData: ResponseData) => responseData?.element?.redirect,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ChatbotRTKService.util.invalidateTags(['Package']));
        } catch {
          // Handle error silently
        }
      },
    }),
  }),
});

export const {
  usePostCancelSubscriptionMutation,
  usePutMakeCardAsDefaultMutation,
  useRemoveSubscriptionCardMutation,
  useGetClientPlansQuery,
  useRedirectToPayModMutation,
  useValidatePromotionCodeMutation,
  useGetPlanInfoQuery,
  useLazyGetPlanInfoQuery,
  useAddCardMutation,
  useGetInvoiceMutation,
  useGetSessionProvideListQuery,
  useProviderPaymentPayModMutation,
} = BillingRTKService;
